import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiSlash } from 'react-icons/fi';
import { cBlocked, cUnblockUser } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import UserAvatar from '../../components/community/UserAvatar';
import UserBadges from '../../components/community/UserBadges';
import { BLUE } from '../../components/community/brand';

/**
 * **គណនីដែលត្រូវបានប្លុក** — the accounts I blocked.
 *
 * While somebody is blocked neither side sees the other's articles, comments,
 * stories or messages, and neither can follow the other. Unblocking brings
 * everything back.
 */
export default function BlockedPage() {
  const { t, lang } = useLanguage();
  const { user, openAuth } = useCommunity();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    cBlocked()
      .then((res) => setItems(res.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [user]);

  const unblock = async (item) => {
    if (!window.confirm(t('unblockConfirm').replace('{u}', item.user?.username || ''))) return;
    try {
      await cUnblockUser(item.user_id);
      setItems((prev) => prev.filter((x) => x.user_id !== item.user_id));
      toast.success(t('unblockedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-3 lg:px-4 py-4">
      <header className="flex items-center gap-2 mb-1">
        <FiSlash className="w-5 h-5 text-gray-400" />
        <h1 className="text-lg font-extrabold">{t('blockedTitle')}</h1>
        <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
          {items.length}
        </span>
      </header>
      <p className="text-xs text-gray-500 mb-3">{t('blockedHint')}</p>

      {!user ? (
        <button
          type="button"
          onClick={() => openAuth('signin', t('verifyNeedLogin'))}
          className="w-full py-3 rounded-2xl text-white font-bold"
          style={{ background: BLUE }}
        >
          {t('verifyNeedLogin')}
        </button>
      ) : loading ? (
        <p className="text-sm text-gray-400">{t('loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">{t('blockedEmpty')}</p>
      ) : (
        <ul className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 p-3">
              <UserAvatar user={item.user} size={40} to={item.user ? `/u/${item.user.username}` : null} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold truncate flex items-center gap-1.5">
                  <span className="truncate">{item.user?.name || item.user?.username}</span>
                  <UserBadges user={item.user} />
                </p>
                <p className="text-[11px] text-gray-400 truncate">
                  @{item.user?.username}{item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString(lang === 'kh' ? 'km-KH' : 'en-GB')}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => unblock(item)}
                className="shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: BLUE }}
              >
                {t('unblockUser')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}